import React, {useEffect, useState} from 'react'
import ButtonLoading from './ButtonLoading'
import agent from '../api/agent'
import {warehouseData, customsTypes, RESHIP_CONVERTABLE_CURRENCIES} from '../common/constants'
import {remove} from 'lodash'
import {Spin, message, Tooltip } from 'antd'
import {SwapOutlined, InfoCircleOutlined} from '@ant-design/icons'
import { round } from '../common/utils'
import {useSelector} from "react-redux";
import UploadIncomingVendorInvoices from './UploadIncomingVendorInvoices'
import handleError from '../utils/handleError'
import usePackageDetails          from '../hooks/usePackage'

const ItemsDeclarationForm = ({pkg, saveAction, setEdit, type, saveValue, closeAction, pkgCustomsType, saveCustoms, onFinish}) => {
  const [declaredItems, setDeclaredItems] = useState([])
  const [customsType, setCustomsType] = useState(!!!pkgCustomsType ? (pkg.outgoingCustomsType ? pkg.outgoingCustomsType : 1 ): pkgCustomsType)
  const [forexRates, setForexRates] = useState([])
  const [savedInvoices, setSavedInvoices] = useState([])

  const { 
    isLoading, 
    updatePackageCustomsType, 
    getItems, 
    updateItems, 
    addItem, 
    deleteItem 
  } = usePackageDetails()

  const itemDeclarationRules = useSelector(state => state.itemDeclarationRules)

  const CONVERTED_TOTAL = round(declaredItems.reduce((acc, val) => acc += val.declaredFxValue * 1, 0))

  const DEFAULT_NEW_ITEM = {
    itemId: 'new',
    declaredName: '',
    declaredDescription: '',
    declaredIncomingVendor: '',
    declaredValue: 0,
    declaredUnitValue: 0,
    declaredQuantity: 0,
    declaredOrigin: warehouseData(pkg.warehouse).countryCode,
    declaredFromCurrency: warehouseData(pkg.warehouse).currency,
    declaredToCurrency: warehouseData(pkg.warehouse).currency,
    declaredFxRate: 1,
    declaredFxValue: 0,
    state: 'pending'
  }

  const [item, setItem] = useState(DEFAULT_NEW_ITEM)

  const IS_DELETE_BUTTON_DISABLED = [8].includes(pkg.disposition)
  
  useEffect(() => {
  }, [item, declaredItems])

  useEffect(() => {
    getCustomsDeclarationData()
  }, [])

  const getCustomsDeclarationData = async () => {
    const items = await getItemsData()
    const forexRates = await getForexData()

    const sanitizedItems = await insertInitialForexRates(items, forexRates)

    if (!sanitizedItems.length) {
      sanitizedItems.push(DEFAULT_NEW_ITEM)
    }

    setDeclaredItems(sanitizedItems)
    setForexRates(forexRates)
    saveAction(items)

    if (saveValue) {
      const total = items.reduce((acc, val) => {
        return acc += val.declaredFxValue * 1
      }, 0)
      saveValue(round(total))
    }
  }

  const insertInitialForexRates = (items, forexRates) => {
    if (items && !items.length) {
      return []
    }

    if (forexRates && !forexRates.length) {
      return []
    }

    const declaredItemsClone = [...items]

    const sanitizedItems = declaredItemsClone.map(item => {

      const selectedForexRate = getForexRate(forexRates, item.declaredToCurrency, item.declaredFromCurrency)
      const rate = handleFXConversion(selectedForexRate)
      const fxValue = round((item.declaredValue * rate))
      return {
        ...item,
        declaredFromCurrency: item.declaredFromCurrency ? item.declaredFromCurrency : warehouseData(pkg.warehouse).currency,
        declaredToCurrency: warehouseData(pkg.warehouse).currency,
        declaredFxRate: rate,
        declaredFxValue: fxValue,
        declaredUnitValue: round(item.declaredValue/item.declaredQuantity, 2)
      }
    })
    return sanitizedItems
  }

  const getForexRate = (forexRates, toCurrency, fromCurrency) => {
    const selectedForexRate = forexRates.find(item => {
      const fxFromCurrency = fromCurrency ? fromCurrency : warehouseData(pkg.warehouse).currency
      const fxToCurrency = toCurrency ? toCurrency : warehouseData(pkg.warehouse).currency

      return item.fromCurrency === fxToCurrency && item.toCurrency === fxFromCurrency
    })
    return selectedForexRate
  }

  const getItemsData = async () => {
    const response = await getItems(pkg)
    const items = response.data

    return items
  }

  const getForexData = async () => {
    const { currency } = warehouseData(pkg.warehouse)
    const response = await agent.forex.getForexRates({ currency })
    const forexRates = response.data.data

    return forexRates
  }

  /**
   * Disables all other inputs if the declared items input is currently in a 'adding-item' state.
   *
   * @param {object} declaredItem
   * @returns boolean
   */
  const isInputDisabled = (declaredItem) => {
    return declaredItems.filter(item => item.state === 'pending' || !!item.lockedForProcessing).length > 0 && declaredItem.state !== 'pending'
  }
  /**
   * Handles all string inputs in the declaration form.
   * If itemId is present, then the number input is associated to 'editing' an existing item,
   * else it is processing input for a new item (add-item state)
   *
   * @param {event} e
   * @returns
   */
  const handleItemStringInput = (e) => {
    try {
      const declaredItemsClone = [...declaredItems]
      const itemId = parseInt(e.target.name.split('-')[0])
      const name = e.target.name.split('-')[1]
      const value = e.target.value.replace(/[^A-Za-z0-9 _]/, '').slice(0, 45)
      const indexOfItemToUpdate = declaredItemsClone.findIndex(item => itemId === item.itemId*1)
      const itemToUpdate = declaredItemsClone[indexOfItemToUpdate]

      itemToUpdate[name] = value

      declaredItemsClone[indexOfItemToUpdate] = itemToUpdate
      setDeclaredItems(declaredItemsClone)

    } catch (e) {
      console.log(e)
    }
  }

  /**
   * Handles all number inputs in the declaration form.
   * If itemId is present, then the number input is associated to 'editing' an existing item,
   * else it is processing input for a new item (add-item state)
   *
   * @param {event} e
   * @returns
   */
  const handleItemNumberInput = (e) => {

    try {
      const declaredItemsClone = [...declaredItems]
      const itemId = parseInt(e.target.name.split('-')[0])
      const name = e.target.name.split('-')[1]
      let value = e.target.value * 1

      if (isNaN(value)) {
        value = 0
      }

      const indexOfItemToUpdate = declaredItemsClone.findIndex(item => itemId === item.itemId*1)
      const itemToUpdate = declaredItemsClone[indexOfItemToUpdate]

      if (name === 'declaredUnitValue') {
        itemToUpdate['declaredValue'] = value*itemToUpdate.declaredQuantity
        itemToUpdate['declaredUnitValue'] = value
      } else {
        itemToUpdate[name] = value
      }

      declaredItemsClone[indexOfItemToUpdate] = itemToUpdate

      const fxValue = round(declaredItemsClone[indexOfItemToUpdate].declaredValue * declaredItemsClone[indexOfItemToUpdate].declaredFxRate)

      declaredItemsClone[indexOfItemToUpdate].declaredFxValue = fxValue

      setDeclaredItems(declaredItemsClone)

    } catch (e) {
      console.log(e)
    }
  }

  const handleDropdownInput = (e) => {
    const declaredItemsClone = [...declaredItems]

    const itemId = parseInt(e.target.name.split('-')[0])
    const name = e.target.name.split('-')[1]
    const value = e.target.value.replace(/[^A-Za-z0-9 _]/, '').slice(0, 45)
    const indexOfItemToUpdate = declaredItemsClone.findIndex(item => itemId === item.itemId*1)
    const itemToUpdate = declaredItemsClone[indexOfItemToUpdate]

    itemToUpdate[name] = value

    declaredItemsClone[indexOfItemToUpdate] = itemToUpdate

    const toCurrency = declaredItemsClone[indexOfItemToUpdate].declaredToCurrency
    const fromCurrency = declaredItemsClone[indexOfItemToUpdate].declaredFromCurrency

    const selectedForexRate = getForexRate(forexRates, toCurrency, fromCurrency)
    const rate = handleFXConversion(selectedForexRate)
    const fxValue = round((declaredItemsClone[indexOfItemToUpdate].declaredValue * rate))
    declaredItemsClone[indexOfItemToUpdate].declaredFxRate = rate
    declaredItemsClone[indexOfItemToUpdate].declaredFxValue = fxValue
    setDeclaredItems(declaredItemsClone)
  }


  /**
   * Since ForexRates only contain conversion of values from CAD,USD,GBP to our supported currencies, to get the proper rate when we want to get the rate
   * for our supported currency converted to CAD,USD,GBP we need to divide the rate by 1 to get the inverted rate
   * @param {} fromCurrency
   * @param {*} toCurrency
   */
  const handleFXConversion = (selectedForexRate) => {
    const rate = 1 / selectedForexRate.rate * 1
    return round(rate)
  }

  /**
   * Locally adds items to the declaredItems array.
   *
   * @returns void
   */
  const addItems = () => {
    const declaredItemsClone = [...declaredItems]

    //control the input to prevent users from adding multiple item rows at one time 
    if (declaredItemsClone.filter(item => item.state === 'pending').length > 0) {
      message.error('Please save your items before trying to add another one')
      return;
    }

    declaredItemsClone.push(DEFAULT_NEW_ITEM)
    setDeclaredItems(declaredItemsClone)
  }


  /**
   * Updates any changes to the declared items
   *
   * @param {array} declaredItems
   */
  const updateDeclaredItem = async (updatedDeclaredItems) => {
    const requestObject = {
      packageId: pkg.packageId,
      items: updatedDeclaredItems,
      customsType
    }
    
    const savedDeclaredItems = updatedDeclaredItems.map(item => ({
      ...item,
      state: null
    }))

    await updateItems(requestObject)
    setDeclaredItems(savedDeclaredItems)
    setItem(DEFAULT_NEW_ITEM)
    // await getCustomsDeclarationData()
  }

  /**
   * Adds the declared item
   *
   * @param {array} declaredItems
   */
  const addDeclaredItem = async (declaredItems) => {

    const addedItem = {
      ...declaredItems[declaredItems.length - 1],
    }

    delete addedItem.state
    delete addedItem.itemId

    const requestObject = {
      packageId: pkg.packageId,
      item: addedItem,
      customsType
    }

    try {
      const response = await addItem(requestObject)
      await getCustomsDeclarationData()
      if (response.status === 200) {
        closeAction()
      } else return

    } catch (e) {

    } finally {
      onFinish && onFinish()
    }
  }

  /**
   * If the declaration form is in an 'add-item' state, then saving will mean adding a new item,
   * if the declaration form is not in an 'add-item' state, then saving will mean updating the items
   * @returns void
   */
  const saveDeclaredItems = async () => {
    try {
      const declaredItemsClone = [...declaredItems]
      //Prevent saving items if there are no declared items to save
      if (!declaredItemsClone.length) {
        return;
      }

      declaredItemsClone.forEach(item => {
        delete item.declaredUnitValue;
      });
      //Prevept saving items if there are no declared items to save
      if (!customsType) {
        message.error('Please declare and save the type of items in your package.')
        return;
      }

      //Prevent processing checkout if declared value is 0
      if (declaredItemsClone.find(item => item.declaredValue === 0)) {
        message.error('Please input item value in declaration form')
        return;
      }

      const addedItem = {...declaredItems[declaredItems.length - 1],}

      if (addedItem.itemId === 'new') {
        await addDeclaredItem(declaredItemsClone)
        return;
      } else  {
        await updateDeclaredItem(declaredItemsClone)
        closeAction()
      }

      onFinish && onFinish()

    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message || 'An error occurred';
      message.error(errorMessage)
    } 
  }

  /**
   * Deletes the declaredItem
   *
   * @param {object} declaredItem
   * @returns
   */
  const deleteDeclaredItem = async (declaredItem) => {

    const declaredItemsClone = [...declaredItems]

    if (IS_DELETE_BUTTON_DISABLED) {
      return;
    }

    if (declaredItemsClone.length === 1 && declaredItemsClone[0].state === 'pending') {
      return;
    }

    if (pkg.locked === 1 || pkg.locked) {
      return
    }

    if (declaredItems.filter(item => item.state === 'pending').length > 0 && declaredItem.state !== 'pending') {
      message.error('Please finish adding the new item entry before deleting previous items')
      return;
    }

    //Removes the item currently queued in the 'add-item' state
    if (!declaredItem.itemId || declaredItem.itemId === 'new') {
      remove(declaredItemsClone, (item) => item.state === 'pending')
      setDeclaredItems(declaredItemsClone)
      return;
    }

    const [removedItem] = remove(declaredItemsClone, (item) => {
      return item.itemId === declaredItem.itemId
    })

    const requestObject = {
      packageId: pkg.packageId,
      itemId: removedItem.itemId
    }

    try {

      await deleteItem(requestObject)
      getCustomsDeclarationData()

    } finally {
      onFinish && onFinish()
    }
  }

  const getItemsTotal = () => {
    const total = declaredItems.reduce((acc, val) => {
      return acc += val.declaredValue * 1
    }, 0)

    return round(total)
  }

  const updateCustomsType = async (value) => {
    if (pkg.locked === 1 || pkg.locked) {
      return
    }

    setCustomsType(value)
    saveCustoms(value)
    await updatePackageCustomsType({ packageId: pkg.packageId, outgoingCustomsType: value})
  }

  const fetchSavedInvoices = async () => {
    try {
      const response = await agent.pkg.getInvoices({ packageId: pkg.packageId });
      setSavedInvoices(response.data.data || []);
    } catch (e) {
      handleError(e)
    }
  };

  const handleDeleteInvoice = async (packageInvoiceId) => {
    try {
      await agent.pkg.deleteInvoice({ packageInvoiceId });
      message.success('Invoice deleted successfully!');
    } catch (error) {
      handleError(error)
    } finally {
      fetchSavedInvoices()
    }
  };

  useEffect(() => {
    fetchSavedInvoices();
  }, [])

  // used for decide display add button or not
  const initItem = declaredItems.find(item => item.itemId === 'new')

  return (
    <div className={`card ${type !== 'checkout' ? `card-white` : `` }`}>
      <div className="package-declaration">
        {type !== 'checkout' && <h5>Package Contents/Declaration</h5>}
        <div className='trans-dec'>
          {!!declaredItems.length &&
            <div>
              <h2 style={{ marginBottom: 5 }}> Contents </h2>
              <span className='ship-card-instructions'> Which of the following best describes the contents of your package? </span>
              <table style={{marginTop: 10}}>
                <tbody>
                  <tr>
                    <td className="btn-toggle">
                      {customsTypes.map(type => {
                        return (
                          <div className={"btn " + (customsType === type.value ? ' btn-selected' : '')}
                              key={'customs-' + type.value} onClick={() => updateCustomsType(type.value)}>{type.label}
                          </div>
                        )
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          }
          <br/>
            <div className="package-declaration-container">
              <Spin spinning={isLoading}>
                <div className="table-responsive">
                  <table className="table" onMouseEnter={() => {setEdit(true)}} onMouseLeave={() => {setEdit(false)}}>
                    {
                      declaredItems.length ? (
                        <thead>
                        <tr>
                          {/* <th scope="col" width="120">Item Name<span style={{color: 'red'}}>*</span></th> */}
                          <th scope="col" width="120">Description<span style={{color: 'red'}}>*</span></th>
                          {itemDeclarationRules?.enableEditIncomingVendor
                           && 
                          <th scope="col" width="120">
                            Incoming Vendor
                            <Tooltip title="For shipments from the US to Canada, enter the vendor you purchased the item from (e.g., Target, Amazon, Walmart) for each item to unlock additional discounted shipping options at checkout.">
                              <InfoCircleOutlined style={{ marginLeft: '8px', fontSize: '16px' }} />
                            </Tooltip>
                          </th>                          
                          }
                          <th scope="col" width="80">Qty<span style={{color: 'red'}}>*</span></th>
                          <th scope="col" width="100">Unit Price<span style={{color: 'red'}}>*</span></th>
                          <th scope="col" width="15"> Item Value</th>
                          <th scope="col" width="85"> Currency</th>
                          <th className="text-center" scope="col" width="50">
                          </th>
                        </tr>
                        </thead>
                      ) : null
                    }
                    <tbody>
                    {
                      declaredItems.map((declaredItem, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <span className="mob-thead">Desc<span style={{color: 'red'}}>*</span></span>
                              <input disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditDescription} style={{border: '1px solid #DCDCDC'}} className="package-item-description-col package-item-input" value={declaredItem.declaredDescription} name={`${declaredItem.itemId}-declaredDescription`} onChange={event => handleItemStringInput(event)}/>
                            </td>
                            {
                              itemDeclarationRules?.enableEditIncomingVendor
                              && 
                            <td>
                              <span className="mob-thead">IncomingVendor<span style={{color: 'red'}}>*</span></span>
                              <input disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditIncomingVendor} style={{border: '1px solid #DCDCDC'}} className="package-item-description-col package-item-input" value={declaredItem.declaredIncomingVendor} name={`${declaredItem.itemId}-declaredIncomingVendor`} onChange={event => handleItemStringInput(event)}/>
                            </td>

                            }
                            <td>
                              <span className="mob-thead">Qty<span style={{color: 'red'}}>*</span></span>
                              <input disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditQty} style={{border: '1px solid #DCDCDC'}} className="package-item-value-col  package-item-input" name={`${declaredItem.itemId}-declaredQuantity`} value={declaredItem.declaredQuantity || ''} onChange={event => handleItemNumberInput(event)}/></td>
                            <td>
                              <span className="mob-thead">Value<span style={{color: 'red'}}>*</span></span>
                              <input 
                                type="number" 
                                step="0.01" 
                                disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditValue} 
                                style={{border: '1px solid #DCDCDC'}} 
                                className="package-item-value-col package-item-input" 
                                name={`${declaredItem.itemId}-declaredUnitValue`}
                                value={declaredItem.declaredUnitValue || ''}
                                onChange={event => handleItemNumberInput(event)} 
                              />
                              </td>
                            <td>
                              <span className="mob-thead">Item Value<span style={{color: 'red'}}>*</span></span>
                              <input disabled={true} style={{border: '1px solid #DCDCDC'}} className="package-item-value-col  package-item-input" value={declaredItem.declaredValue  || ''} />
                            </td>
                            <td>
                              <select style={{border: '1px solid #DCDCDC'}}
                                      className="package-item-value-col package-item-input"
                                      defaultValue={declaredItem.declaredFromCurrency ? declaredItem.declaredFromCurrency : warehouseData(pkg.warehouse).currency}
                                      name={`${declaredItem.itemId}-declaredFromCurrency`}
                                      disabled={pkg.locked === 1 || pkg.locked || !itemDeclarationRules?.enableEditCurrency}
                                      onChange={handleDropdownInput}>
                                {
                                  RESHIP_CONVERTABLE_CURRENCIES.map((item, i) => {
                                    return (
                                      <option value={item} key={`fromCurrency-${item}-${i}`}> {item} </option>
                                    )
                                  })
                                }
                              </select>
                            </td>

                            {/* <td className="text-center"><span className="mob-thead">Delete</span>
                              <button className="package-item-icon-col" disabled={!itemDeclarationRules?.enableDeleteItem} onClick={() => deleteDeclaredItem(declaredItem)}><i><svg class="feather"><use href="/assets/images/icons/feather-sprite.svg#trash-2"/></svg></i></button>
                            </td> */}
                          </tr>
                        )
                      })
                    }
                    {(type === 'checkout' && !!!initItem && itemDeclarationRules?.enableAddItem) &&
                      <tr>
                      <td colSpan="6" className="text-center">
                        <div className="btn-add" onClick={addItems}><i><svg class="feather"><use href="/assets/images/icons/feather-sprite.svg#plus-circle"/></svg></i> Add Item</div>
                      </td>
                    </tr>}
                    <tr>
                      <td colSpan="6">
                        <table className="package-total">
                          <tr>
                            <td style={{border: 'none'}} className="ship-item-total-title">Total Value</td>
                            <td style={{border: 'none'}}>{getItemsTotal() || ''}</td>
                            <td style={{textAlign: 'center', border: 'none'}}><SwapOutlined/></td>
                            <td style={{border: 'none'}}> {CONVERTED_TOTAL} {(warehouseData(pkg.warehouse).currency)}  </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </Spin>
            </div>
            {(!pkg.locked && type !== 'checkout') &&
              <>
                <div className="flex justify-content-end mt-4">
                  <button className={`btn btn-outline-primary`} onClick={saveDeclaredItems}><i
                    className="material-icons">save</i> {isLoading ? <ButtonLoading/> : 'Save'} </button>
                </div>
              </>
            }
          {(!pkg.locked && type === 'checkout') &&
            <>
              <div style={{ display: "flex", justifyContent: 'space-between'}} className="mt-4">
                <div>
                  {
                    itemDeclarationRules?.enableEditIncomingVendor
                     && 
                     <>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      {
                          itemDeclarationRules?.enableEditIncomingVendor
                          && 
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <UploadIncomingVendorInvoices
                            packageId={pkg.packageId} 
                            savedInvoices={savedInvoices}
                            callback={fetchSavedInvoices}
                          />
                        </div>
                        }
         
                      </div>
                      {savedInvoices.length > 0 && (
                        <div style={{ paddingBottom: '5px' }}>
                          {savedInvoices.map(({ packageInvoiceId, url, fileName }) => (
                            <div key={packageInvoiceId} className="package-details-item">
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                {fileName}
                              </a>
                              <button
                                className="package-item-icon-col"
                                style={{ paddingLeft: '5px' }}
                                onClick={() => handleDeleteInvoice(packageInvoiceId)}
                              >
                                <i>
                                  <svg className="feather">
                                    <use href="/assets/images/icons/feather-sprite.svg#trash-2" />
                                  </svg>
                                </i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                     </>
                  }
                </div>

                <div>
                  <button className="btn btn-green" onClick={saveDeclaredItems}>{isLoading ? <ButtonLoading/> : `Confirm Declaration & Pay Shipping`} </button>
                </div>
              </div>
            </>
          } 
        </div>
      </div>
    </div>
  )
}

export default ItemsDeclarationForm
